<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ResponseCard from '../../assistant/ResponseCard.vue';

const POLLING_INTERVAL = 3000;
const MAX_POLL_ATTEMPTS = 20;

const props = defineProps({
  captainDocument: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);

const uiFlags = useMapGetter('captainResponses/getUIFlags');
const responses = useMapGetter('captainResponses/getRecords');
const meta = useMapGetter('captainResponses/getMeta');
const isFetching = computed(() => uiFlags.value.fetchingList);
const totalCount = computed(() => meta.value.totalCount || 0);
const pollAttempts = ref(0);
const isPolling = ref(false);
let pollingTimer = null;

const isGenerating = computed(
  () => (isFetching.value || isPolling.value) && totalCount.value === 0
);
const showEmptyState = computed(
  () => !isFetching.value && !isPolling.value && totalCount.value === 0
);

const fetchResponses = () =>
  store.dispatch('captainResponses/get', {
    assistantId: props.captainDocument.assistant.id,
    documentId: props.captainDocument.id,
  });

const clearPolling = () => {
  if (pollingTimer) {
    clearTimeout(pollingTimer);
    pollingTimer = null;
  }
  isPolling.value = false;
};

const schedulePolling = () => {
  if (pollAttempts.value >= MAX_POLL_ATTEMPTS || totalCount.value > 0) {
    clearPolling();
    return;
  }

  isPolling.value = true;
  pollingTimer = setTimeout(async () => {
    pollAttempts.value += 1;
    await fetchResponses();
    schedulePolling();
  }, POLLING_INTERVAL);
};

const handleClose = () => {
  clearPolling();
  emit('close');
};

onMounted(async () => {
  await fetchResponses();
  schedulePolling();
});

onUnmounted(() => {
  clearPolling();
});
defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="`${t('CAPTAIN.DOCUMENTS.RELATED_RESPONSES.TITLE')} (${totalCount})`"
    :description="t('CAPTAIN.DOCUMENTS.RELATED_RESPONSES.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    width="3xl"
    @close="handleClose"
  >
    <div
      v-if="isGenerating"
      class="flex min-h-48 flex-col items-center justify-center gap-4 py-10 text-center text-n-slate-11"
    >
      <div class="relative flex items-center justify-center">
        <div class="absolute h-16 w-16 animate-ping rounded-full bg-n-alpha-2" />
        <div class="relative rounded-full bg-n-slate-3 p-4">
          <Spinner />
        </div>
      </div>
      <div class="space-y-1">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.DOCUMENTS.RELATED_RESPONSES.GENERATING_TITLE') }}
        </p>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.DOCUMENTS.RELATED_RESPONSES.GENERATING_SUBTITLE') }}
        </p>
      </div>
    </div>
    <div
      v-else-if="showEmptyState"
      class="flex min-h-48 items-center justify-center py-10 text-sm text-n-slate-11"
    >
      {{ t('CAPTAIN.DOCUMENTS.RELATED_RESPONSES.EMPTY') }}
    </div>
    <div v-else class="flex flex-col gap-3 min-h-48">
      <ResponseCard
        v-for="response in responses"
        :id="response.id"
        :key="response.id"
        :question="response.question"
        :status="response.status"
        :answer="response.answer"
        :assistant="response.assistant"
        :created-at="response.created_at"
        :updated-at="response.updated_at"
        compact
      />
    </div>
  </Dialog>
</template>
